import { useState, type FormEvent } from 'react';
import { submitLead } from '../lib/leads';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const VERTICALS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Select one…' },
  { value: 'law_enforcement', label: 'Law enforcement' },
  { value: 'education', label: 'K-12 / Higher ed' },
  { value: 'fire_ems', label: 'Fire / EMS' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'corporate_security', label: 'Corporate security' },
  { value: 'emergency_management', label: 'Emergency management' },
  { value: 'other', label: 'Other' },
];

/**
 * Inline early-access form embedded on the marketing landing. Captures
 * the same identity fields as the demo modal — name, work email, phone,
 * organization (agency), role, vertical — so sales can actually reach
 * the lead instead of just having an email address.
 */
export function EarlyAccessForm(): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState('');
  const [role, setRole] = useState('');
  const [vertical, setVertical] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      await submitLead({
        type: 'early_access',
        name,
        email,
        phone: phone || undefined,
        organization: organization || undefined,
        role: role || undefined,
        vertical,
      });
      setStatus('success');
      setName('');
      setEmail('');
      setPhone('');
      setOrganization('');
      setRole('');
      setVertical('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  if (status === 'success') {
    return (
      <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-4">
        <p className="text-sm font-semibold text-navy-900">
          You&rsquo;re on the list.
        </p>
        <p className="mt-1 text-sm text-steel-600">
          We&rsquo;ll reach out as soon as your vertical opens.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <Field
        id="ea-name"
        label="Name"
        required
        value={name}
        onChange={setName}
        autoComplete="name"
      />
      <Field
        id="ea-email"
        label="Work email"
        required
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        placeholder="you@agency.gov"
      />
      <Field
        id="ea-phone"
        label="Phone"
        type="tel"
        value={phone}
        onChange={setPhone}
        autoComplete="tel"
        placeholder="555-123-4567"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field
          id="ea-org"
          label="Agency / Organization"
          value={organization}
          onChange={setOrganization}
          autoComplete="organization"
        />
        <Field
          id="ea-role"
          label="Your role"
          value={role}
          onChange={setRole}
          placeholder="Training officer"
        />
      </div>
      <div>
        <label
          htmlFor="ea-vertical"
          className="block text-xs font-semibold text-steel-700 uppercase tracking-wider"
        >
          Vertical <span className="text-ember-500 ml-0.5">*</span>
        </label>
        <select
          id="ea-vertical"
          required
          value={vertical}
          onChange={(e) => setVertical(e.target.value)}
          className="mt-1.5 block w-full rounded-md border border-steel-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
        >
          {VERTICALS.map((v) => (
            <option key={v.value} value={v.value}>
              {v.label}
            </option>
          ))}
        </select>
      </div>
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="btn btn-outline-dark w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === 'submitting' ? 'Submitting…' : 'Get early access'}
      </button>
      {status === 'error' && (
        <p className="text-xs text-ember-600">{errorMsg}</p>
      )}
    </form>
  );
}

function Field({
  id,
  label,
  type = 'text',
  required = false,
  value,
  onChange,
  autoComplete,
  placeholder,
}: {
  id: string;
  label: string;
  type?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold text-steel-700 uppercase tracking-wider"
      >
        {label}
        {required && <span className="text-ember-500 ml-0.5">*</span>}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        autoComplete={autoComplete}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 block w-full rounded-md border border-steel-300 bg-white px-3 py-2.5 text-sm placeholder:text-steel-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
      />
    </div>
  );
}
